import cn from 'classnames';
import { FC, ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type {
  ConsultationStatus,
  Document,
  DocumentSectionUpdateRequest,
} from '@/shared/api/transcriptor';
import { Button } from '@/shared/ui';

import type {
  DocumentTemplateSummary,
  EncounterSchema,
} from '@/entities/templates';
import type { TranscriptorConsultation } from '@/entities/transcriptor';

import { getAddableDocuments, getSchemaDocumentLabel } from '../lib';

import { DocumentEditor } from './DocumentEditor';
import { DocumentOffPanel } from './DocumentOffPanel';
import { DocumentTabs, type DocumentTab } from './DocumentTabs';
import styles from './EncounterDocuments.module.scss';
import { InlineConfirm } from './InlineConfirm';

type Props = {
  encounter: TranscriptorConsultation;
  schema: EncounterSchema | undefined;
  documentTemplates: DocumentTemplateSummary[];
  activeKey: string;
  canEdit: boolean;
  locked: boolean;
  className?: string;
  onActiveKeyChange: (key: string) => void;
  onSave: (
    document: Document,
    sections: DocumentSectionUpdateRequest[],
  ) => Promise<void>;
  onDirtyChange: (dirty: boolean) => void;
  // Receives the regular text editor of the document to keep or replace.
  renderDocument?: (document: Document, editor: ReactNode) => ReactNode;
  // Further tools of a document's header in the regular text editor.
  renderActions?: (document: Document) => ReactNode;
  onGenerateDocument?: (templateSeriesId: string) => Promise<void>;
  // The status the encounter's header right above already shows.
  encounterStatus?: ConsultationStatus;
};

const ADD_KEY_PREFIX = 'add:';
const ACTIVE_ENCOUNTER_STATUSES = ['created', 'recording', 'processing'];

const documentKey = (document: Document) => document.id ?? document.code ?? '';

const additionKey = (templateSeriesId?: string | null) =>
  `${ADD_KEY_PREFIX}${templateSeriesId}`;

export const EncounterDocuments: FC<Props> = ({
  encounter,
  schema,
  documentTemplates,
  activeKey,
  canEdit,
  locked,
  className,
  onActiveKeyChange,
  onSave,
  onDirtyChange,
  renderDocument: renderCustomDocument,
  renderActions,
  onGenerateDocument,
  encounterStatus,
}) => {
  const [confirmingSeriesId, setConfirmingSeriesId] = useState<string>();
  const [startingSeriesId, setStartingSeriesId] = useState<string>();
  const [failedSeriesId, setFailedSeriesId] = useState<string>();
  const documents = encounter.documents;
  const encounterActive = ACTIVE_ENCOUNTER_STATUSES.includes(encounter.status);
  const generate = canEdit ? onGenerateDocument : undefined;
  const addable = generate
    ? getAddableDocuments({ schema, templates: documentTemplates, documents })
    : [];
  const selectedAddition = addable.find(
    (reference) => additionKey(reference.template_series_id) === activeKey,
  );
  // An added document replaces its "+" tab, so the addition key also selects it.
  const activeDocument = selectedAddition
    ? undefined
    : documents.find(
        (item) =>
          documentKey(item) === activeKey ||
          additionKey(item.template_series_id) === activeKey,
      ) ?? documents[0];
  const activeAddition = activeDocument
    ? undefined
    : selectedAddition ?? addable[0];

  if (!activeDocument && !activeAddition) {
    return (
      <p className={cn('p3', styles.pending, className)}>
        <FormattedMessage
          id="workspace.documentPending"
          defaultMessage="The document will appear after processing."
        />
      </p>
    );
  }

  const start = async (templateSeriesId: string) => {
    if (!generate || startingSeriesId) {
      return;
    }
    setStartingSeriesId(templateSeriesId);
    setFailedSeriesId(undefined);
    try {
      await generate(templateSeriesId);
      setConfirmingSeriesId(undefined);
    } catch {
      setFailedSeriesId(templateSeriesId);
    } finally {
      setStartingSeriesId(undefined);
    }
  };

  const renderAddition = (templateSeriesId: string, label: string) => (
    <DocumentOffPanel
      title={label}
      error={
        failedSeriesId === templateSeriesId && (
          <FormattedMessage
            id="workspace.document.generateFailed"
            defaultMessage="Could not start AI processing. Try again."
          />
        )
      }
    >
      <p className="p3">
        <FormattedMessage
          id="workspace.document.addLater"
          defaultMessage="This document is not part of the encounter yet. Add it to generate it from this encounter’s recording."
        />
      </p>
      {confirmingSeriesId === templateSeriesId ? (
        <InlineConfirm
          message={
            <FormattedMessage
              id="workspace.document.addConfirm"
              defaultMessage="Add “{name}”? This runs AI processing for this document only; the other documents stay as they are."
              values={{ name: label }}
            />
          }
          confirmLabel={
            <FormattedMessage
              id="workspace.document.runAi"
              defaultMessage="Run AI processing"
            />
          }
          pending={startingSeriesId === templateSeriesId}
          onConfirm={() => start(templateSeriesId)}
          onCancel={() => {
            setConfirmingSeriesId(undefined);
            setFailedSeriesId(undefined);
          }}
        />
      ) : (
        <Button
          variant="secondary"
          size="medium"
          disabled={locked || encounterActive}
          onClick={() => setConfirmingSeriesId(templateSeriesId)}
        >
          <FormattedMessage
            id="workspace.document.addAndGenerate"
            defaultMessage="Add and generate"
          />
        </Button>
      )}
    </DocumentOffPanel>
  );

  const renderDocument = (document: Document) => {
    const templateSeriesId = document.template_series_id;
    const requirement = schema?.documents.find(
      (reference) =>
        reference.template_series_id === templateSeriesId ||
        reference.code === document.code,
    )?.inclusion;
    const editor = (
      <DocumentEditor
        key={`${encounter.id}/${document.id}`}
        document={document}
        canEdit={canEdit}
        requirement={requirement}
        actions={renderActions?.(document)}
        onSave={(sections) => onSave(document, sections)}
        onDirtyChange={onDirtyChange}
        onRegenerate={
          generate && templateSeriesId && !encounterActive && !locked
            ? () => generate(templateSeriesId)
            : undefined
        }
        encounterStatus={encounterStatus}
      />
    );
    return renderCustomDocument
      ? renderCustomDocument(document, editor)
      : editor;
  };

  return (
    <DocumentTabs
      className={className}
      tabs={[
        ...documents.map(
          (item): DocumentTab => ({
            key: documentKey(item),
            label: item.tab_title || item.name || item.code || '',
            included: true,
            mark:
              item.status === 'completed' || item.status === 'edited'
                ? 'ready'
                : item.status === 'generating' || item.status === 'pending'
                  ? 'pending'
                  : undefined,
          }),
        ),
        ...addable.map(
          (reference): DocumentTab => ({
            key: additionKey(reference.template_series_id),
            label: getSchemaDocumentLabel(reference, documentTemplates),
            included: false,
          }),
        ),
      ]}
      activeKey={
        activeDocument
          ? documentKey(activeDocument)
          : additionKey(activeAddition?.template_series_id)
      }
      disabled={locked}
      onChange={onActiveKeyChange}
    >
      {activeDocument
        ? renderDocument(activeDocument)
        : activeAddition?.template_series_id &&
          renderAddition(
            activeAddition.template_series_id,
            getSchemaDocumentLabel(activeAddition, documentTemplates),
          )}
    </DocumentTabs>
  );
};
